import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronDown, LogOut, Settings2, ShieldCheck } from 'lucide-react';
import { Popover } from '../ui/Popover';
import { Segmented } from '../ui/Segmented';
import { PreferencesDialog, THEME_OPTIONS } from './PreferencesDialog';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import { ROLE_LABELS } from '../../config';

const itemClass =
  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-fg-2 transition-colors hover:bg-raised hover:text-fg';

/** Account menu: who is signed in, theme, preferences, resources, admin, sign out. */
export const UserMenu = () => {
  const { user, isAdmin, signOut } = useAuth();
  const prefs = usePreferences();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();

  return (
    <>
      <Popover
        panelLabel="Account menu"
        trigger={({ props, open }) => (
          <button
            type="button"
            {...props}
            className="flex h-9 items-center gap-2 rounded-lg px-1.5 text-sm text-fg-2 transition-colors hover:bg-raised hover:text-fg"
            aria-label={`Account menu for ${user?.username}`}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent-fg" aria-hidden="true">
              {initials}
            </span>
            <span className="hidden max-w-[8rem] truncate lg:inline">{user?.username}</span>
            <ChevronDown size={14} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} aria-hidden="true" />
          </button>
        )}
      >
        {({ close }) => (
          <div className="flex flex-col">
            <div className="px-2.5 pb-2.5 pt-1.5">
              <p className="truncate text-sm font-medium text-fg">{user?.username}</p>
              <p className="text-xs text-muted">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
            <div className="border-t border-line px-2.5 py-2.5">
              <p className="mb-1.5 text-xs font-medium text-faint" id="theme-label">Theme</p>
              <Segmented
                label="Theme"
                size="sm"
                value={prefs.theme}
                onChange={(v) => prefs.setPreference('theme', v)}
                options={THEME_OPTIONS}
                stretch
              />
            </div>
            <div className="flex flex-col border-t border-line pt-1.5">
              <button type="button" className={itemClass} onClick={() => { close(); setPrefsOpen(true); }}>
                <Settings2 size={16} aria-hidden="true" /> Preferences…
              </button>
              <Link to="/resources" className={itemClass} onClick={close}>
                <BookOpen size={16} aria-hidden="true" /> Resources &amp; links
              </Link>
              {isAdmin && (
                <Link to="/admin" className={itemClass} onClick={close}>
                  <ShieldCheck size={16} aria-hidden="true" /> Admin
                </Link>
              )}
              <button
                type="button"
                className={`${itemClass} text-danger hover:text-danger`}
                onClick={() => { close(); signOut(); }}
              >
                <LogOut size={16} aria-hidden="true" /> Sign out
              </button>
            </div>
          </div>
        )}
      </Popover>
      <PreferencesDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </>
  );
};
